import { Button, Paper, Step, StepButton, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import React, { Component } from 'react';
import { WizardConfig, WizardStep } from "../../models/wizard";
import { JQueryValidationService } from '../../services/JQueryValidationService';

/**
 * Wizard class component, outputs a MUI Stepper component.
 * @see Stepper
 */
export class Wizard extends Component<{ config: WizardConfig }, { stepIndex: number, steps: Array<WizardStep> }> {

	/**
	 * Initialises a new instance of the Wizard class.
	 * @param props Configuration properties.
	 */
    constructor(props: { config: WizardConfig }) {
        super(props);

		// create initial state
        this.state = {
            stepIndex: this.getActiveStepIndex(),
            steps: props.config.steps
        };
    }

	/**
	 * Event handler called after the component is inserted into the DOM.
	 */
    componentDidMount() {

        this.insertStepContent();

        if (this.props.config.activeStepElement && !this.props.config.activeStepElement.value) {

            this.setActiveStepElementValue(this.state.stepIndex);
        }
    }

	/**
	 * Event handler called after state has been updated.
	 */
    componentDidUpdate() {

        this.insertStepContent(true);
    }

	/**
	 * Function which navigates to a specific wizard step.
	 * @param step The wizard step to navigate to.
	 */
	navigate(step: WizardStep) {

		if (step.navigatable && this.getActiveStepIndex() !== step.index) {

			const currentIndex = this.getActiveStepIndex();
			const form = this.props.config.targetElement.closest("form");

			if (form) {

				// if going forwards the current form must be valid
				const jForm = $(form);
				if (currentIndex < step.index && (jForm.data(JQueryValidationService.validatorData) && jForm.valid() === false)) {

					return;
				}
			}

			this.setActiveStepElementValue(step.index);
			this.setState({
				stepIndex: step.index
			});

			// submit parent form if there is one
			if (form && form.hasAttribute("data-ajax")) {

				const event = new Event("submit", {
					cancelable: true
				});
				form.dispatchEvent(event);
			}
		}
	}

	/**
	 * Sets the active wizard step.
	 * @param stepIndex The new step index.
	 */
    setActiveStep(stepIndex: number) {

        this.setActiveStepElementValue(stepIndex);

        this.setState({
            stepIndex
        });
    }

	/**
	 * Inserts the active content element into the corresponding wizard step element.
	 * @param isUpdate Determines whether this call is this an update or initial insert.
	 */
    private insertStepContent(isUpdate: boolean = false) {

        const target = this.props.config.targetElement;
        let stepsContainer = target.querySelector("[data-steps-container]");
        let activeStepElement = target.querySelector("[data-active-step]");

        if (!stepsContainer) {

            stepsContainer = document.createElement("div");
            stepsContainer.setAttribute("data-steps-container", "");
            target.appendChild(stepsContainer);
        }

        if (!activeStepElement && this.props.config.activeStepElement) {

            target.appendChild(this.props.config.activeStepElement);
        }

        let visibleIndex = 0;

		// loop each wizard step
        this.state.steps.forEach((step: WizardStep) => {

            if (isUpdate === true && step.visible === false) {

                return true;
            }

			// if this is the visible step
            if (visibleIndex === this.state.stepIndex) {

                const contentTarget = document.getElementById(step.guid);

                if (contentTarget) {

					// append the content element to the step
                    contentTarget.appendChild(step.content);

					// now check for buttons that navigate backwards and forward
                    step.content.querySelectorAll("[data-step-forward],[data-step-backward]").forEach(btn => {

                        (btn as HTMLElement).onclick = event => {

							// handle forward button click
                            if (btn.hasAttribute("data-step-forward")) {

                                this.forward();
							}
							// handle backward button click
                            else {

                                this.backward();
							}

                            event.preventDefault();
                        };
                    });

                    // attempt to set focus to the first input element with [autofocus] in the step
                    let focusElement = step.content.querySelector("[autofocus]");
                    if (focusElement instanceof HTMLElement) {

                        (focusElement as HTMLElement).focus();
                    }
                    else {

                        // set focus to the first input element in the step
                        focusElement = step.content.querySelector("input,select,button,a");

                        if (focusElement instanceof HTMLElement) {

                            (focusElement as HTMLElement).focus();
                        }
                    }
                }
            }
            else {

				// otherwise place the content back in the step container
                stepsContainer?.appendChild(step.content);
            }

            visibleIndex++;
        });
    }

	/**
	 * Set the active step element field value by index. The value is looked up by index.
	 * @param stepIndex The active step index.
	 */
    private setActiveStepElementValue(stepIndex: number) {

        if (this.props.config.activeStepElement) {

            const visibleSteps = this.props.config.steps.filter(step => step.visible);

            if (stepIndex < visibleSteps.length) {

                this.props.config.activeStepElement.value = visibleSteps[stepIndex].key;
            }
        }
    }

	/**
	 * Gets the active step index.
	 */
    private getActiveStepIndex() {

        let stepIndex = 0;

        const activeStepKey = this.props.config.activeStepElement?.value;

        if (activeStepKey) {

            const visibleSteps = this.props.config.steps.filter(step => step.visible);

            visibleSteps.forEach((step, index) => {

                if (step.key === activeStepKey) {

                    stepIndex = index;
                }
            });
        }

        return stepIndex;
    }

	/**
	 * Navigate forward through the stepper by 1 step.
	 */
    forward() {

        this.setActiveStep(this.state.stepIndex + 1);
    };

	/**
	 * Navigate backward through the stepper by 1 step.
	 */
    backward() {

        this.setActiveStep(this.state.stepIndex - 1);
    }

	/**
	 * Resets the step back to the 1st available step.
	 */
    reset() {

        this.setActiveStep(0);
    }

	// output the configured MUI stepper
    render() {
		return (
			<React.Fragment>
                <Stepper activeStep={this.state.stepIndex} orientation="vertical" nonLinear>
                    {this.state.steps.filter(step => step.visible).map(step =>
						<Step key={step.key} completed={step.completed}>
                            <StepButton onClick={() => this.navigate(step)} className="form-no-validate">
                                {step.title}
                            </StepButton>
                            <StepContent>
                                <div id={step.guid}></div>
                            </StepContent>
                        </Step>
                    )}
                </Stepper>
                {this.state.stepIndex === this.state.steps.length && (
                    <Paper square elevation={0}>
                        <Typography>All steps completed - you&apos;re finished</Typography>
                        <Button onClick={() => this.reset()}>
						Reset
                    </Button>
                    </Paper>
                )}
			</React.Fragment>
        );
    }
}
